import {Controller, FieldValues, useFieldArray, UseFormSetValue, useWatch} from "react-hook-form";
import {Control, UseFormRegister} from "react-hook-form/dist/types/form";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import {Autocomplete, FormLabel, Grid} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {WriteContractForm, WriteProductForm} from "./WriteContract";
import {useAllDict} from "@/store/dict.c";
import {ConfirmationNumberOutlined, ControlPointOutlined, CurrencyYuanOutlined, RemoveCircleOutlineOutlined} from '@mui/icons-material';
import {GroupProduct, GroupProductType} from "@/widget/GroupProduct/GroupProduct";
import React from "react";
import {SearchData} from "@/widget/SearchData/SearchData";
import {SearchChanceVO} from "@/service/chance.s";
import { str2arr} from "@/helper/method";
import {get, isNumber} from "lodash";
import {DictPO} from "@/model/system";
import {TitleBar} from "@/widget/TitleBar/TitleBar";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import isEmpty from "lodash/isEmpty";

interface ProductFormProps<T extends FieldValues> {
    control: Control<WriteContractForm>;
    register: UseFormRegister<WriteContractForm>;
    errors: FieldErrors<WriteContractForm>;
    setValue: UseFormSetValue<any | T>;
}

const ProductForm = <T extends FieldValues, >({control, register, errors, setValue}: ProductFormProps<T>) => {
        const {fields, append, remove, update} = useFieldArray({
            name: "products",
            control,
        });
        const {isRetard, patentType, copyrightType, trademarkType, projectType} = useAllDict()

        const handleSelectChance = (chance?: SearchChanceVO) => {
            if (chance) {
                return {
                    productNames: `${chance?.productType},${chance?.kind}`,
                    productIds: `${chance?.productTypeId},${chance?.kindId}`,
                    num: chance?.amount,
                    price: chance?.money,
                    kind: chance?.kind,
                    kindId: chance?.kindId,
                    category: chance?.category,
                    categoryId: chance?.categoryId,
                    productTypeId: chance?.productTypeId,
                    productType: chance?.productType,
                }
            }
            return {}
        }

        const getProductCategory = (productId: string, productName: string): Omit<WriteProductForm, "price" | "num"> => {
            const productIds = str2arr(productId)
            const productNames = str2arr(productName, "string")
            return {
                productTypeId: get(productIds, "[0]", 0),
                productType: get(productNames, "[0]", ''),
                kindId: get(productIds, "[1]", 0),
                kind: get(productNames, "[1]", ''),
                productIds: productId,
            } as WriteProductForm
        }
        const customerId = useWatch({
            control: control,
            name: "customerId"
        })

        return (
            <>
                <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignContent={"center"}
                    alignItems={'center'}
                >
                    <TitleBar title={"服务信息"} extra={<IconButton
                        size={"small"}
                        onClick={() => append({num: 1, price: '', productIds: '', category: ''})}
                        color={'default'}
                        sx={{padding: 0}}
                    >
                        <ControlPointOutlined/>
                    </IconButton>}/>
                </Stack>
                {fields.map((field, index) => {
                    const {productTypeId} = field
                    let applyType: DictPO[] = []
                    if (productTypeId == 1) {
                        applyType = patentType
                    } else if (productTypeId == 2) {
                        applyType = projectType
                    } else if (productTypeId == 3) {
                        applyType = trademarkType
                    } else if (productTypeId == 4) {
                        applyType = copyrightType
                    }
                    return (
                        <Stack
                            width={'100%'}
                            direction={"row"}
                            id={field.id}
                            key={field.id}
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            alignContent={"center"}
                        >
                            <Grid spacing={2} container width={'inherit'}>
                                <Grid size={6}>
                                    <SearchData<WriteContractForm, SearchChanceVO> label={'关联商机'} tag={3}
                                                                                   onSelect={(searchChance?: SearchChanceVO) => {
                                                                                       return update(index, {...field, ...handleSelectChance(searchChance)})
                                                                                   }}
                                                                                   attrId={`products.${index}.chanceId`} query={{customerId}} autoLoad={isEmpty(customerId)}
                                                                                   readOnly={!isNumber(customerId)} setValue={setValue}
                                                                                   attr={`products.${index}.chanceName`}
                                                                                   control={control}/>
                                </Grid>
                                <Grid size={6}>
                                    <GroupProduct<GroupProductType> errMsg={errors?.products?.[index]?.productIds?.message} setValue={setValue} label={'服务类型'}
                                                                    attrId={`products.${index}.productIds`}
                                                                    attr={`products.${index}.productNames`}
                                                                    onSelect={(item) =>
                                                                        update(index, {
                                                                            ...field,
                                                                            productNames: isEmpty(item) ? item : `${item?.group},${item?.label}`,
                                                                            ...getProductCategory(`${item?.value}`, `${item?.group},${item?.label}`)
                                                                        })
                                                                    }
                                                                    control={control}/>
                                </Grid>
                                {productTypeId != 5 && <> <Grid size={6}>
                                    <FormControl fullWidth={true} required={true}>
                                        <FormLabel>服务名称</FormLabel>
                                        <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                            options={applyType || []}
                                            value={value as any}
                                            onChange={(_, option: DictPO | null) => {
                                                setValue(`products.${index}.categoryId`, option?.value || null);
                                                onChange(option?.label || null)
                                            }}
                                            fullWidth
                                            renderInput={(params) => (
                                                <TextField error={!!errors?.products?.[index]?.category}
                                                           helperText={errors?.products?.[index]?.category?.message}
                                                           variant="outlined"
                                                           placeholder={"选择服务名称"} {...params} />
                                            )}
                                        />} name={`products.${index}.category`}/>
                                    </FormControl>
                                </Grid>
                                    <Grid size={6}>
                                        <FormControl fullWidth={true} required={true}>
                                            <FormLabel htmlFor="price">价格</FormLabel>
                                            <TextField
                                                error={!!errors?.products?.[index]?.price}
                                                helperText={errors?.products?.[index]?.price?.message}
                                                id="price"
                                                {...register(`products.${index}.price`)}
                                                slotProps={{
                                                    input: {
                                                        inputProps: {maxLength: 10},
                                                        endAdornment: (
                                                            <CurrencyYuanOutlined color="disabled"/>
                                                        ),
                                                    },
                                                }}
                                                placeholder="填写价格(单价)"
                                                variant="outlined"
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid size={6}>
                                        <FormControl fullWidth={true} required={true}>
                                            <FormLabel htmlFor="num">数量</FormLabel>
                                            <Controller control={control} render={({field: {onChange, value}}) => <TextField
                                                error={!!errors?.products?.[index]?.num}
                                                helperText={errors?.products?.[index]?.num?.message}
                                                value={value}
                                                onChange={event => onChange(parseInt(event.target.value) || null)}
                                                placeholder="填写数量"
                                                variant="outlined"
                                                slotProps={{
                                                    input: {
                                                        inputProps: {maxLength: 2},
                                                        endAdornment: (
                                                            <ConfirmationNumberOutlined color="disabled"/>
                                                        ),
                                                    },
                                                }}
                                            />} name={`products.${index}.num`}/>
                                        </FormControl>
                                    </Grid>
                                    <Grid size={6}>
                                        <FormControl fullWidth={true}>
                                            <FormLabel>内部期限</FormLabel>
                                            <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                                <DatePicker
                                                    showYearDropdown={true}
                                                    showMonthDropdown={true}
                                                    dateFormat="yyyy/MM/dd"
                                                    selected={value as Date}
                                                    onChange={date => onChange(date)}
                                                    locale='zhCN'
                                                    placeholderText={"选择内部期限"}
                                                    customInput={<TextField sx={{
                                                        width: "100%",
                                                        "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                                    }}
                                                    />}
                                                />
                                            </DatePickers>} name={`products.${index}.insideDeadline`}/>
                                        </FormControl>
                                    </Grid>
                                    {productTypeId == 1 &&
                                        <Grid size={6}>
                                            <FormControl fullWidth={true} required={true}>
                                                <FormLabel>是否减缓</FormLabel>
                                                <Controller control={control} render={({field: {onChange, value}}) => <Autocomplete
                                                    options={isRetard}
                                                    value={value as any}
                                                    onChange={(_, option: DictPO | null) => {
                                                        setValue(`products.${index}.retardId`, option?.value);
                                                        onChange(option?.label)
                                                    }}
                                                    fullWidth
                                                    renderInput={(params) => (
                                                        <TextField error={!!errors?.products?.[index]?.retardId}
                                                                   helperText={errors?.products?.[index]?.retardId?.message}
                                                                   variant="outlined"
                                                                   placeholder={"选择是否减缓"} {...params} />
                                                    )}
                                                />} name={`products.${index}.retard`}/>
                                            </FormControl>
                                        </Grid>

                                    }</>}
                            </Grid>
                            {
                                fields?.length > 1 ? (
                                    <IconButton
                                        onClick={() => remove(index)}
                                        sx={{padding: 0}}
                                        size={"small"}
                                    >
                                        <RemoveCircleOutlineOutlined color={"error"}/>
                                    </IconButton>
                                ) : <></>
                            }
                        </Stack>
                    )
                        ;
                })}
            </>
        );
    }
;

export default ProductForm;
